import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Trash2 } from 'lucide-react';
import type { ChPostPrint } from '../types';

interface Props {
  prints: ChPostPrint[];
  onRemove?: (print: ChPostPrint) => void;
  /** Thumbnail size class; defaults to a compact row. */
  thumbClassName?: string;
}

export function PrintsGallery({ prints, onRemove, thumbClassName = 'w-20 h-20' }: Props) {
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') setOpen(i => (i === null ? i : Math.min(i + 1, prints.length - 1)));
      if (e.key === 'ArrowLeft') setOpen(i => (i === null ? i : Math.max(i - 1, 0)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, prints.length]);

  if (prints.length === 0) return null;
  const current = open !== null ? prints[open] : null;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {prints.map((print, i) => (
          <div key={print.id} className={`relative group ${thumbClassName}`}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="w-full h-full rounded-xl overflow-hidden bg-gray-100 ring-1 ring-black/5"
              title={print.caption || 'Ver print'}
            >
              <img src={print.url} alt={print.caption || ''} className="w-full h-full object-cover" loading="lazy" />
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(print)}
                className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-white shadow text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                title="Remover print"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {current && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
          {open! > 0 && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setOpen(open! - 1); }}
              className="absolute left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          <figure className="max-w-4xl max-h-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img src={current.url} alt={current.caption || ''} className="max-h-[80vh] max-w-full object-contain rounded-lg" />
            <figcaption className="mt-3 text-sm text-white/70 text-center">
              {current.caption && <span className="text-white">{current.caption} · </span>}
              {open! + 1} / {prints.length}
            </figcaption>
          </figure>
          {open! < prints.length - 1 && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setOpen(open! + 1); }}
              className="absolute right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Seguinte"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>
      )}
    </>
  );
}
